import { withDependencies, multi, named, optional } from '@wix/thunderbolt-ioc'
import { name, PopHistoryStateHandler, UrlChangeHandlerForPage } from './symbols'
import type {
	IAppWillMountHandler,
	BrowserWindow,
	ViewerModel,
	ISamePageUrlChangeListener,
	IAppWillLoadPageHandler,
	ILogger,
	NavigationParams,
	Experiments,
	IPageProvider,
} from '@wix/thunderbolt-symbols'
import {
	CurrentRouteInfoSymbol,
	BrowserWindowSymbol,
	ViewerModelSym,
	SamePageUrlChangeListenerSymbol,
	MasterPageFeatureConfigSymbol,
	LoggerSymbol,
	FeatureExportsSymbol,
	ExperimentsSymbol,
	SiteServicesManagerSymbol,
	SiteFeatureConfigSymbol,
	PageProviderSymbol,
} from '@wix/thunderbolt-symbols'
import type {
	IUrlHistoryPopStateHandler,
	IUrlHistoryManager,
	IUrlChangeHandler,
	ICurrentRouteInfo,
	RouterMasterPageConfig,
} from './types'
import type { IFeatureExportsStore } from 'thunderbolt-feature-exports'
import { createUrlHistoryManager } from '@wix/url-manager'
import type { ServicesManager } from '@wix/services-manager/types'
import { UrlDefinition } from '@wix/viewer-service-url/definition'
import type { IRoutingConfig } from '@wix/thunderbolt-types'
import type { IMpaNavigation } from 'feature-mpa-navigation'
import { MpaNavigationSymbol } from 'feature-mpa-navigation'

export const UrlChangeListener = withDependencies(
	[PageProviderSymbol, CurrentRouteInfoSymbol],
	(pageProvider: IPageProvider, currentRouteInfo: ICurrentRouteInfo): ISamePageUrlChangeListener => {
		return {
			onUrlChange: async (url) => {
				const routeInfo = currentRouteInfo.getCurrentRouteInfo()
				if (routeInfo) {
					currentRouteInfo.updateRouteInfoUrl(url)
					const { contextId, pageId } = routeInfo
					const page = await pageProvider(contextId, pageId)
					const pageHandlers = page.getAllImplementersOf<IUrlChangeHandler>(UrlChangeHandlerForPage)
					return Promise.all(pageHandlers.map((handler) => handler.onUrlChange(url)))
				}
			},
		}
	}
)

export const UrlHistoryManager = withDependencies(
	[
		named(MasterPageFeatureConfigSymbol, name),
		named(FeatureExportsSymbol, name),
		BrowserWindowSymbol,
		ViewerModelSym,
		multi(SamePageUrlChangeListenerSymbol),
		CurrentRouteInfoSymbol,
		LoggerSymbol,
		ExperimentsSymbol,
		named(SiteFeatureConfigSymbol, name),
		optional(SiteServicesManagerSymbol),
	],
	(
		{ popupPages }: RouterMasterPageConfig,
		routerExports: IFeatureExportsStore<typeof name>,
		browserWindow: BrowserWindow,
		viewerModel: ViewerModel,
		samePageUrlChangeListeners: Array<ISamePageUrlChangeListener>,
		currentRouteInfo: ICurrentRouteInfo,
		logger: ILogger,
		experiments: Experiments,
		siteConfig: IRoutingConfig,
		siteServicesManager?: ServicesManager
	): IAppWillLoadPageHandler & IUrlHistoryManager => {
		const isBuilder = siteConfig.isBuilderComponentModel
		const isServicesInfra = experiments['specs.thunderbolt.servicesInfra'] || isBuilder
		const shouldUseUrlService = isServicesInfra && siteServicesManager && siteServicesManager.hasService(UrlDefinition)
		const getUrlServiceApi = () => {
			const urlService = siteServicesManager!.getService(UrlDefinition)
			samePageUrlChangeListeners.forEach((listener) => urlService.addOnUrlChangeListener(listener))
			return {
				...urlService,
				pushUrlState: (parsedUrl: URL, navigationParams: Omit<NavigationParams, 'anchorDataId'> = {}) => {
					urlService.pushUrlState?.(parsedUrl, navigationParams)
					routerExports.export({
						currentUrl: urlService.getFullUrlWithoutQueryParams(),
						currentUrlWithQueryParams: urlService.getCurrentUrl(),
						relativeUrl: urlService.getRelativeUrl(),
					})
				},
			}
		}
		const getUrlHistoryManagerApi = () => {
			const urlManagerApi = createUrlHistoryManager({
				browserWindow,
				requestUrl: viewerModel.requestUrl,
				externalBaseUrl: viewerModel.site.externalBaseUrl,
				onUrlChangeListeners: samePageUrlChangeListeners,
				currentRouteInfo,
				logger,
				isUpdateHrefInSafariIncognitoOpen: !!experiments['specs.thunderbolt.update_href_in_safari_incognito'],
			})
			return {
				pushUrlState: (parsedUrl: URL, navigationParams: Omit<NavigationParams, 'anchorDataId'> = {}) => {
					urlManagerApi.pushUrl(parsedUrl, navigationParams)
					routerExports.export({
						currentUrl: urlManagerApi.api.getFullUrlWithoutQueryParams(),
						currentUrlWithQueryParams: urlManagerApi.getCurrentUrl(),
						relativeUrl: urlManagerApi.api.getRelativeUrl(),
					})
				},
				...urlManagerApi.api,
				...urlManagerApi,
			}
		}
		const api = shouldUseUrlService ? getUrlServiceApi() : getUrlHistoryManagerApi()

		routerExports.export({
			addOnUrlChangeListener: api.addOnUrlChangeListener,
			removeOnUrlChangeListener: api.removeOnUrlChangeListener,
		})

		return {
			name: 'urlManager',
			appWillLoadPage({ pageId }) {
				// Popups are triggering appWillLoadPage without updating the currentRouteInfo, so we ignore it for
				// deciding whether to samePageUrlChangeListener.onUrlChange() (Otherwise we loose the same page
				// context after a popup is opened).
				if (popupPages[pageId]) {
					return
				}

				// Keeping track of previous page ID so we know when url is changed within the same page (Assuming
				// pushUrlState happens after this appWillLoadPage).
				logger.updatePageId(pageId)
				api.updateState(pageId)
			},
			...api,
		}
	}
)

export const PopStateListener = withDependencies(
	[
		multi(PopHistoryStateHandler),
		BrowserWindowSymbol,
		multi(SamePageUrlChangeListenerSymbol),
		CurrentRouteInfoSymbol,
		ExperimentsSymbol,
		optional(MpaNavigationSymbol),
	] as const,
	(
		popStateHandlers: Array<IUrlHistoryPopStateHandler>,
		browserWindow: BrowserWindow,
		samePageUrlChangeListeners: Array<ISamePageUrlChangeListener>,
		currentRouteInfo: ICurrentRouteInfo,
		experiments: Experiments,
		mpaNavigation?: IMpaNavigation
	): IAppWillMountHandler => ({
		appWillMount: () => {
			if (!browserWindow) {
				return
			}
			const isAutoScrollingOnMpaEnabled = experiments['specs.thunderbolt.autoScrollingOnIphoneMPA']
			const isMpaEligible = !!mpaNavigation?.isEligible({ isLoggingEnabled: false })
			if (browserWindow.history) {
				// for multi page aplications we dont want to use manual scrollRestoration
				// https://wix.atlassian.net/browse/TB-13956
				if (!isAutoScrollingOnMpaEnabled) {
					browserWindow.history.scrollRestoration = 'manual'
				}
			}

			browserWindow.addEventListener('popstate', async () => {
				const href = browserWindow.location.href
				// for single page aplications we want to use manual scrollRestoration, in theory this script should run only in SPA but we want to be safe
				// https://wix.atlassian.net/browse/TB-5187
				if (isAutoScrollingOnMpaEnabled && !isMpaEligible) {
					browserWindow.history.scrollRestoration = 'manual'
				}
				await Promise.all(popStateHandlers.map((handler) => handler.onPopState(new URL(href))))

				const pageIdBeforeHandlingPopState = currentRouteInfo.getPreviousRouterInfo()?.pageId
				const pageIdAfterHandlingPopState = currentRouteInfo.getCurrentRouteInfo()?.pageId
				// when the first url change is due to a navigation to a tpa section, and the back button is hit, there's no prev route info
				if (!pageIdBeforeHandlingPopState || pageIdBeforeHandlingPopState === pageIdAfterHandlingPopState) {
					samePageUrlChangeListeners.forEach((listener) => listener.onUrlChange(new URL(href)))
				}
			})
		},
	})
)
